import type { SafetyWindow } from '@magicschool/business-logic';
import { toast } from '@magicschool/spellbook';
import type { Enums, Tables } from '@magicschool/supabase/types';
import { useEffect, useMemo, useRef } from 'react';
import { useIntl } from 'react-intl';
import {
  type ClosingHours,
  determineRoomStatus,
  determineSchedulingStatus,
  determineStatusBasedOnScheduling,
  getClosingHours,
  getHoursOnlyClosure,
  getRoomWindow,
  type RoomAvailabilityRow,
} from '../_helpers/determineRoomStatus';
import type { RoomSchedulingStatus } from '../types';
import { useSchedulingLabel } from './_shared/useSchedulingLabel';
import { useSchedulingTimer } from './_shared/useSchedulingTimer';

export interface UseRoomSchedulingStatusParams {
  room?: Partial<Tables<'rooms'>> | null;
  interval?: number;
  status?: Enums<'room_state'>;
  showSchedulingToasts?: boolean;
  /** The org's open window, when the caller has it; the room's own hours are read from `room`. */
  orgWindow?: SafetyWindow | null;
}

export interface UseRoomSchedulingStatusReturn {
  schedulingStatus: RoomSchedulingStatus;
  label: React.ReactNode;
  status: Enums<'room_state'>;
  /** The room's own recurring window, so surfaces can describe it alongside the date schedule. */
  roomWindow: SafetyWindow | null;
  /** The room's state and date schedule alone, before any hours apply. */
  statusWithoutHours: Enums<'room_state'>;
  /** The hours closing the room right now, even when the teacher's state also closes it. */
  closingHours: ClosingHours | null;
  /** True when hours are the only thing keeping students out. */
  closedByHours: boolean;
  /** When students can next use the room, when only hours stand in the way and the time is known. */
  opensAt: Date | null;
}

/**
 * Hook to determine and update the scheduling status of a room at regular intervals.
 * @param params.room - The room to determine scheduling status for
 * @param params.interval - How often to update the status in milliseconds (default: 60000ms)
 * @param params.status - Optional override for the room's current status (useful for student views)
 * @returns The current scheduling status, a localized label for display, and the computed room status
 */
export const useRoomSchedulingStatus = ({
  room,
  status,
  interval = 60_000,
  showSchedulingToasts = false,
  orgWindow = null,
}: UseRoomSchedulingStatusParams): UseRoomSchedulingStatusReturn => {
  const roomSafe = room || {};
  const intl = useIntl();
  const previousStatusRef = useRef<RoomSchedulingStatus | null>(null);

  // Display callers hand in whatever slice of the room they hold; absent hours columns read as unrestricted here,
  // never on the server, where the selects are typed against RoomAvailabilityRow.
  const roomRow = useMemo(
    (): RoomAvailabilityRow => ({
      state: status || roomSafe.state || 'locked',
      scheduling_enabled: roomSafe.scheduling_enabled ?? false,
      scheduled_start_at: roomSafe.scheduled_start_at ?? null,
      scheduled_end_at: roomSafe.scheduled_end_at ?? null,
      open_days: roomSafe.open_days ?? null,
      open_start_time: roomSafe.open_start_time ?? null,
      open_end_time: roomSafe.open_end_time ?? null,
      timezone: roomSafe.timezone ?? null,
    }),
    [roomSafe, status],
  );
  const roomWindow = useMemo(() => getRoomWindow(roomRow), [roomRow]);
  const windows = useMemo(() => [roomWindow, orgWindow], [roomWindow, orgWindow]);
  const now = useSchedulingTimer({ room, interval, windows });
  const schedulingStatus = useMemo(() => determineSchedulingStatus(now, roomSafe), [now, roomSafe]);
  const messages = useMemo(
    () => ({
      started: intl.formatMessage({ id: 'room.scheduling-started.toast' }),
      ended: intl.formatMessage({ id: 'room.scheduling-ended.toast' }),
    }),
    [intl],
  );

  useEffect(() => {
    if (!showSchedulingToasts || !roomSafe.scheduling_enabled) return;

    const previousStatus = previousStatusRef.current;
    if (previousStatus && previousStatus !== schedulingStatus) {
      if (previousStatus === 'not-started' && schedulingStatus === 'active') {
        toast({ title: messages.started, status: 'info' });
      } else if (previousStatus === 'active' && schedulingStatus === 'ended') {
        toast({ title: messages.ended, status: 'info' });
      }
    }
    previousStatusRef.current = schedulingStatus;
  }, [schedulingStatus, roomSafe.scheduling_enabled, showSchedulingToasts, messages]);

  // Display fails open on an unparseable window; the server fails closed.
  const { computedStatus, statusWithoutHours, closingHours, closedByHours, opensAt } = useMemo(() => {
    const options = { safetyWindow: orgWindow, onError: 'open' } as const;
    const computed = determineRoomStatus(now, roomRow, options);
    const closing = computed === 'archived' ? null : getClosingHours(now, roomRow, options);
    const hoursOnly = closing && getHoursOnlyClosure(now, roomRow, { status: 'loaded', window: orgWindow }, closing);
    return {
      computedStatus: computed,
      statusWithoutHours: determineStatusBasedOnScheduling(schedulingStatus, roomRow.state),
      closingHours: closing,
      closedByHours: hoursOnly !== null,
      opensAt: hoursOnly?.opensAt ?? null,
    };
  }, [now, roomRow, schedulingStatus, orgWindow]);

  const label = useSchedulingLabel(schedulingStatus, roomSafe);

  return {
    schedulingStatus,
    label,
    status: computedStatus,
    roomWindow,
    statusWithoutHours,
    closingHours,
    closedByHours,
    opensAt,
  };
};
